import React from 'react';
import { Modal } from '@components/molecules/Modal';
import { Progress } from '@components/atoms/Progress';
import {
  getSeatCountingRoleLabels,
  getNonSeatCountingRoleLabels,
} from '@constants/seatCountingRoles';
import { useTranslation } from '@hooks/useTranslation';

interface SeatUsageModalProps {
  isOpen: boolean;
  onClose: () => void;
  occupiedSeats: number;
  totalSeats: number;
  /** Role names that occupy a seat under the current plan. Undefined = no plan data. */
  seatCountingRoles?: string[];
}

const RolePills: React.FC<{ labels: string[] }> = ({ labels }) => (
  <div className="flex flex-wrap gap-1.5">
    {labels.map((label) => (
      <span
        key={label}
        className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
        {label}
      </span>
    ))}
  </div>
);

export const SeatUsageModal: React.FC<SeatUsageModalProps> = ({
  isOpen,
  onClose,
  occupiedSeats,
  totalSeats,
  seatCountingRoles,
}) => {
  const tSeats = useTranslation('billing').seats;
  const tEnumLabels = useTranslation('enumLabels');
  const tCommon = useTranslation('common');

  const availableSeats = totalSeats - occupiedSeats;
  const isOverLimit = occupiedSeats > totalSeats;
  const isFull = availableSeats <= 0 && !isOverLimit;
  const percent = totalSeats > 0 ? (occupiedSeats / totalSeats) * 100 : 0;

  const hasRoleData = !!seatCountingRoles;
  const usesSeat = getSeatCountingRoleLabels(seatCountingRoles, tEnumLabels, tCommon.unknown);
  const freeSeat = hasRoleData
    ? getNonSeatCountingRoleLabels(seatCountingRoles, tEnumLabels, tCommon.unknown)
    : [];

  const progressVariant = isOverLimit || isFull ? 'error' : availableSeats <= 2 ? 'warning' : 'success';

  const availabilityCaption = isOverLimit
    ? tSeats.overSeatLimitCaption
    : isFull
    ? tSeats.noSeatsAvailableCaption
    : tSeats.seatsAvailableSuffix.replace('{n}', String(availableSeats));

  const footerText = !hasRoleData
    ? tSeats.billedPerSeatFooter
    : freeSeat.length === 0
    ? tSeats.allRolesCountFooter
    : tSeats.someRolesFreeFooter;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={tSeats.seatUsageModalTitle} size="small">
      <div className="flex flex-col gap-5 pb-2">
        {/* Seat math */}
        <div>
          <p className="text-base font-semibold text-gray-900 dark:text-white mb-2">
            {tSeats.seatsUsedSuffix
              .replace('{active}', String(occupiedSeats))
              .replace('{total}', String(totalSeats))}
          </p>
          <Progress value={percent} variant={progressVariant} size="md" />
          <p
            className={`text-xs mt-1.5 ${
              isOverLimit || isFull
                ? 'text-red-600 dark:text-red-400'
                : availableSeats <= 2
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
            {availabilityCaption}
          </p>
        </div>

        {/* Role breakdown (only when we have plan data) */}
        {hasRoleData && usesSeat.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              {tSeats.usesSeatHeading}
            </p>
            <RolePills labels={usesSeat} />
          </div>
        )}

        {hasRoleData && freeSeat.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              {tSeats.freeNoSeatHeading}
            </p>
            <RolePills labels={freeSeat} />
          </div>
        )}

        {/* Explanation */}
        <p className="text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
          {footerText}
        </p>
      </div>
    </Modal>
  );
};
